import type { FeaturedVideo, LibraryItem } from '@/shared/types';
import { deleteJson, getJson, postJson } from '@/shared/api/apiClient';

/*
 * Items the user added to their library from the browse page.
 *
 * These are curated videos the account does not own, so they cannot live in the
 * generated-video library `/api/v1/videos` returns — and they cannot be a
 * reference to one either: the underlying job belongs to the curation account
 * and `/videos/{id}` is owner-only, so a saved item would 404 for the person who
 * saved it. Everything the card needs is therefore denormalised at save time
 * and stored as a copy, in `/api/v1/saved-content`.
 *
 * `durationSeconds` here IS a real runtime, unlike the API's `duration` on a
 * job. Same word, third meaning — see JobMetadata.runtime_seconds.
 */

const PATH = '/api/v1/saved-content';

export type SavedContent = {
  id: string;
  title: string;
  description?: string;
  videoUrl: string;
  thumbnailUrl?: string;
  /** Captions, carried from the featured feed — the content page cannot fetch
      them later (the job belongs to the curation account, and /videos/{id} is
      owner-only), so save time is the only time to keep them. */
  vttUrl?: string;
  durationSeconds?: number;
  tags?: string[];
  savedAt: string;
};

/** What the API takes on write — the same copy, by its wire names. */
function toPayload(video: FeaturedVideo) {
  return {
    id: video.id,
    title: video.title,
    description: video.description,
    videoUrl: video.videoUrl,
    thumbnailUrl: video.thumbnailUrl,
    vttUrl: video.vttUrl,
    durationSeconds: video.durationSeconds,
    tags: video.tags,
  };
}

export async function listSavedContent(_userId: string): Promise<SavedContent[]> {
  return getJson<SavedContent[]>(PATH);
}

export async function saveContent(
  _userId: string,
  video: FeaturedVideo,
): Promise<SavedContent[]> {
  // Re-saving is a no-op server-side rather than an update: `savedAt` orders the
  // rail, and saving something twice should not move it to the top.
  return postJson<SavedContent[]>(PATH, toPayload(video));
}

/**
 * Re-point saved records at a freshly fetched feed.
 *
 * The stored URLs are CloudFront delivery URLs today, but nothing says they stay
 * that way (delivery_url falls back to presigned S3, which expires), and a
 * re-encoded or re-captioned curated video changes keys. Every browse visit
 * already holds the fresh truth, so fold it in: cheap, and it means a saved card
 * can only go stale while the video is also gone from the feed.
 *
 * The feed is sent rather than re-derived server-side because the client already
 * has it in hand at exactly this moment.
 *
 * **The caller decides whether this is worth doing at all.** `BrowsePage` calls
 * it only once it knows the viewer has something saved, because there is nothing
 * to re-point otherwise and this posts the entire feed. That guard cannot live
 * here: the service would have to read the saved list to know, which is the
 * round-trip it would be trying to avoid.
 *
 * The empty-feed case below is the one it *can* answer alone, and it is a plain
 * read rather than a no-op because the caller still wants the list back. As
 * localStorage that read was free; over HTTP it is a request, so a caller with
 * neither a feed nor a reason to re-read should call `listSavedContent` and skip
 * this entirely.
 */
export async function refreshSavedContent(
  userId: string,
  feed: FeaturedVideo[],
): Promise<SavedContent[]> {
  if (feed.length === 0) return listSavedContent(userId);
  return postJson<SavedContent[]>(`${PATH}/refresh`, feed.map(toPayload));
}

export async function unsaveContent(
  _userId: string,
  videoId: string,
): Promise<SavedContent[]> {
  return deleteJson<SavedContent[]>(`${PATH}/${encodeURIComponent(videoId)}`);
}

export function isSaved(saved: SavedContent[], videoId: string): boolean {
  return saved.some((item) => item.id === videoId);
}

/**
 * Presents saved content as a library item so the grid can render one list.
 *
 * `status: 'completed'` because a curated video is always watchable — it was
 * never generated by this account, so it has no job lifecycle. `createdAt` uses
 * the save time, which is why the card labels these "Added ..." and reserves
 * "Created" for videos this account actually generated.
 */
export function savedAsLibraryItem(item: SavedContent): LibraryItem {
  return {
    id: item.id,
    title: item.title,
    description: item.description ?? '',
    videoUrl: item.videoUrl,
    thumbnailUrl: item.thumbnailUrl,
    vttUrl: item.vttUrl,
    durationSeconds: item.durationSeconds,
    tags: item.tags,
    status: 'completed',
    createdAt: item.savedAt,
  };
}
